import type { SupportedLangCode } from '@/application/i18n/language'

/** Default host of the Piper voices: the upstream `rhasspy/piper-voices`
 * Hugging Face repository. Overridable through `VITE_PIPER_VOICES_BASE_URL`
 * so a mirror repository (same `<revision>/<path>` layout) can be used later
 * without touching the code. */
export const DEFAULT_PIPER_VOICES_BASE_URL = 'https://huggingface.co/rhasspy/piper-voices/resolve'

/** Pinned commit of `rhasspy/piper-voices` (never `main`), so the model bytes
 * — and therefore the checksums below — can never change under us. */
export const PIPER_VOICES_REVISION = 'c10ece1aade47bb51c153c893d14e5bf8e5b7117'

/** One downloadable Piper voice — exactly one per content language, the same
 * for every visitor on every platform. */
export interface PiperVoiceEntry {
  /** Piper voice key, e.g. `fr_FR-siwis-medium`. */
  id: string
  lang: SupportedLangCode
  /** Human-readable name shown in the menu. */
  displayName: string
  modelUrl: string
  configUrl: string
  revision: string
  /** SHA-256 (hex) of the `.onnx` model, verified before it is cached. */
  sha256: string
  sizeBytes: number
  /** SHA-256 (hex) of the `.onnx.json` config. */
  configSha256: string
  license: string
  /** Credit line shown in the audio-credits page. */
  attribution: string
  /** True when the licence legally requires the attribution to be shown. */
  attributionRequired: boolean
}

interface VoiceSource {
  id: string
  lang: SupportedLangCode
  displayName: string
  path: string
  sha256: string
  sizeBytes: number
  configSha256: string
  license: string
  attribution: string
  attributionRequired: boolean
}

const VOICE_SOURCES: readonly VoiceSource[] = [
  {
    id: 'fr_FR-siwis-medium',
    lang: 'fr',
    displayName: 'Siwis (fr_FR)',
    path: 'fr/fr_FR/siwis/medium/fr_FR-siwis-medium',
    sha256: '641d1ab097da2b81128c076810edb052b385decc8be3381814802a64a73baf99',
    sizeBytes: 63201294,
    configSha256: '39479916c2db192b5ac9764daddd0c744d83e023ad890c6976c0633ae4df8959',
    license: 'CC BY 4.0',
    attribution:
      'SIWIS French Speech Synthesis Database, Idiap / University of Edinburgh, CC BY 4.0',
    attributionRequired: true,
  },
  {
    id: 'en_GB-cori-medium',
    lang: 'en',
    displayName: 'Cori (en_GB)',
    path: 'en/en_GB/cori/medium/en_GB-cori-medium',
    sha256: '1899f98e5fb8310154f3c2973f4b8a929ba7245e722b3d3a85680b833d95f10d',
    sizeBytes: 63531379,
    configSha256: 'e262c16d7f192f69d4edd6b4ef8a5915379e67495fcc402f1ab15eeb33da3d36',
    license: 'Public domain (LibriVox)',
    attribution: 'Bryce Beattie / LibriVox',
    attributionRequired: false,
  },
  {
    id: 'es_ES-davefx-medium',
    lang: 'es',
    displayName: 'DaveFX (es_ES)',
    path: 'es/es_ES/davefx/medium/es_ES-davefx-medium',
    sha256: '6658b03b1a6c316ee4c265a9896abc1393353c2d9e1bca7d66c2c442e222a917',
    sizeBytes: 63201294,
    configSha256: '0e0dda87c732f6f38771ff274a6380d9252f327dca77aa2963d5fbdf9ec54842',
    license: 'CC0',
    attribution: 'DaveFX',
    attributionRequired: false,
  },
]

/** Builds the catalogue against `baseUrl` (`<base>/<revision>/<path>`). Pure,
 * so tests can exercise a mirror URL. */
export function createVoiceCatalogue(
  baseUrl: string = DEFAULT_PIPER_VOICES_BASE_URL,
  revision: string = PIPER_VOICES_REVISION,
): Record<SupportedLangCode, PiperVoiceEntry> {
  const root = (baseUrl || DEFAULT_PIPER_VOICES_BASE_URL).replace(/\/+$/, '')
  const entries = VOICE_SOURCES.map((source): [SupportedLangCode, PiperVoiceEntry] => [
    source.lang,
    {
      id: source.id,
      lang: source.lang,
      displayName: source.displayName,
      modelUrl: `${root}/${revision}/${source.path}.onnx`,
      configUrl: `${root}/${revision}/${source.path}.onnx.json`,
      revision,
      sha256: source.sha256,
      sizeBytes: source.sizeBytes,
      configSha256: source.configSha256,
      license: source.license,
      attribution: source.attribution,
      attributionRequired: source.attributionRequired,
    },
  ])
  return {
    fr: entries[0][1],
    en: entries[1][1],
    es: entries[2][1],
  }
}

export const VOICE_CATALOGUE = createVoiceCatalogue(import.meta.env.VITE_PIPER_VOICES_BASE_URL)

/** Catalogue entry for `lang`. */
export function getVoiceEntry(lang: SupportedLangCode): PiperVoiceEntry {
  return VOICE_CATALOGUE[lang]
}

/** Approximate download size of one voice, in whole megabytes (for the
 * consent prompt) — the smallest of the catalogue, so the figure never
 * overstates what a visitor downloads. */
export function approxVoiceSizeMb(): number {
  const smallest = Math.min(...Object.values(VOICE_CATALOGUE).map((entry) => entry.sizeBytes))
  return Math.round(smallest / 1_000_000)
}
